import {Menu} from '@dropbox/dig-components/menu';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {atoms, Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AiSearchLine} from '@dropbox/dig-icons/assets';
import {GlobalHeader} from '@dropbox/dpc-ia';
import {logNavItemClicked} from 'analytics/navAnalytics';
import {pulseUserAtom} from 'atoms/auth';
import {isMobileAtom} from 'atoms/layout';
import {filterAtom, showSearchAtom} from 'atoms/search';
import {ROUTE_PATHS} from 'constant';
import {reportAndLogError} from 'helpers/logging';
import {isOSX} from 'helpers/platform';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {useLocation, useNavigate} from 'react-router';
import {getBackendUrl, getLoginService, getWaitlistUrl} from 'utilities';
import {clearNavMenuSnapshots} from 'views/knowledge/navMenuSnapshot';
import {useWorkstream} from 'views/workstreams/hooks';
import {clearLandingSnapshots} from 'views/workstreams/page/landingSnapshot';

import {AiSearchMinimizedNavChip} from './ai/AiSearchMinimizedNavChip';
import {AppHelpMenuActions, AppSuperUserMenuSegment} from './AppHelpMenuSegments';
import {ConstructionCrew} from './ConstructionCrew';
import {Avatar} from './DSYS/Avatar';
import {CompanyOSLogo} from './DSYS/CompanyOSLogo';
import {PersonHoverCard} from './DSYS/HoverCard';
import {Link} from './DSYS/Link';
import {NotificationButton} from './notifications/NotificationButton';

const Navbar = () => {
  const isMobile = useAtomValue(isMobileAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const pulseUser = useAtomValue(pulseUserAtom);
  const setShowSearch = useSetAtom(showSearchAtom);
  const setFilter = useSetAtom(filterAtom);

  // prefetch employee workstreams for profile page
  useWorkstream(pulseUser?.ldap, undefined, undefined, {refetchOnWindowFocus: false});

  const handleLogout = () => {
    clearLandingSnapshots();
    clearNavMenuSnapshots();
    getLoginService()
      .routeLogoutAndRemoveCookieApiV1AuthLogoutGet()
      .then(() => {})
      .catch((error) => {
        reportAndLogError(error, 'Failed to logout');
      })
      .finally(() => {
        window.location.href = getWaitlistUrl();
        // window.location.href = getLoginUrl() + '&prompt=login';
      });
  };

  const handleSearchClick = () => {
    setFilter('all');
    setShowSearch('nav');
  };

  return (
    <GlobalHeader.Wrapper
      className="pulse-AppGlobalHeader"
      style={{boxSizing: 'border-box', paddingRight: '12px'}}
    >
      <Link
        to={ROUTE_PATHS.HOME}
        style={{height: 35}}
        state={{source: 'navbar'}}
        onClick={() =>
          logNavItemClicked({
            item: 'logo',
            path: ROUTE_PATHS.HOME,
            location: 'navbar',
            isCurrent: location.pathname === ROUTE_PATHS.HOME,
          })
        }
      >
        <CompanyOSLogo mobile={isMobile} />
      </Link>

      {!isMobile && <ConstructionCrew />}

      <Box marginLeft="auto">
        <Split alignY="center" gap="8">
          <Split.Item>
            <TextInput
              onClick={handleSearchClick}
              onKeyDown={handleSearchClick}
              tabIndex={-1}
              placeholder={t('search_global_short')}
              withLeftAccessory={<UIIcon src={AiSearchLine} />}
              withRightAccessory={
                <Box color="Border Base">
                  {isOSX ? t('search_shortcut_mac') : t('search_shortcut_windows')}
                </Box>
              }
            />
          </Split.Item>
          <Split.Item>
            <AiSearchMinimizedNavChip />
          </Split.Item>
          <Split.Item>
            <NotificationButton />
          </Split.Item>
          <Split.Item>
            {!pulseUser ? (
              <Skeleton.Avatar size="small" className={atoms({marginTop: '4'})} />
            ) : (
              <GlobalHeader.AccountMenu
                src={getBackendUrl() + pulseUser?.profile_photo_url}
                initials={pulseUser?.abbreviated_name ?? ''}
                triggerProps={{
                  'aria-label': t('account_menu'),
                }}
              >
                <Menu.Segment>
                  {pulseUser?.ldap ? (
                    <PersonHoverCard
                      ldap={pulseUser.ldap}
                      name={pulseUser.display_name ?? pulseUser.ldap}
                      profilePhotoUrl={pulseUser.profile_photo_url}
                    >
                      <Link to={'/people/' + pulseUser.ldap} hasNoUnderline>
                        <Menu.ActionItem
                          withLeftAccessory={<Avatar user={pulseUser} size="large" />}
                          withSubtitle={pulseUser.email}
                          withTitle={pulseUser.display_name}
                        ></Menu.ActionItem>
                      </Link>
                    </PersonHoverCard>
                  ) : null}
                </Menu.Segment>
                <Menu.Segment>
                  <Menu.ActionItem onClick={() => navigate(ROUTE_PATHS.SETTINGS)}>
                    {t('settings')}
                  </Menu.ActionItem>
                  <AppHelpMenuActions />
                </Menu.Segment>
                <AppSuperUserMenuSegment />
                <Menu.Segment>
                  <Menu.ActionItem onClick={handleLogout}>{t('sign_out')}</Menu.ActionItem>
                </Menu.Segment>
              </GlobalHeader.AccountMenu>
            )}
          </Split.Item>
        </Split>
      </Box>
    </GlobalHeader.Wrapper>
  );
};

export {Navbar};
